import { useRef, useState } from "react";
import { resetPassword } from "../services/auth";
import { getApiErrorMessage } from "../utils/Util";
import { useLanguage } from "../context/LanguageContext";

interface Props {
  token: string;
}

function ResetPasswordPopup({ token }: Props) {
  const { t } = useLanguage();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);

  const handleSubmit = async () => {
    if (newPassword !== confirmPassword) {
      setPasswordError(t("register.passwordMismatch"));
      return;
    }
    setPasswordError("");
    setError("");
    setLoading(true);
    try {
      await resetPassword(newPassword, token);
      setSuccess(true);
      setTimeout(() => closeRef.current?.click(), 2500);
    } catch (err) {
      setError(getApiErrorMessage(err, t("resetPassword.error")));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="modal fade"
      id="resetPasswordModal"
      tabIndex={-1}
      aria-hidden="true"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "400px" }}>
        <div className="modal-content rounded-4 shadow">
          <button
            ref={closeRef}
            type="button"
            className="btn-close position-absolute top-0 end-0 m-3"
            data-bs-dismiss="modal"
            aria-label={t("common.close")}
            style={{ zIndex: 1 }}
          />

          <div className="modal-body px-4 py-4 text-center">
            <div className="mb-2">
              <i className="bi bi-shield-lock-fill" style={{ fontSize: "3rem", color: "#c20e1a" }}></i>
            </div>
            <h6 className="fw-bold fs-5 mb-1">{t("resetPassword.title")}</h6>
            <p className="text-muted small mb-4">{t("resetPassword.subtitle")}</p>

            {success ? (
              <div className="py-2">
                <i className="bi bi-check-circle-fill text-success" style={{ fontSize: "2.5rem" }}></i>
                <p className="mt-2 fw-semibold">{t("resetPassword.successMessage")}</p>
                <p className="text-muted small">{t("common.windowClosing")}</p>
              </div>
            ) : (
              <>
                <div className="mb-3 text-start">
                  <label className="form-label fw-semibold">{t("resetPassword.newPassword")}</label>
                  <div className="input-group">
                    <span className="input-group-text"><i className="bi bi-lock"></i></span>
                    <input
                      type="password"
                      className="form-control"
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={(e) => { setNewPassword(e.target.value); if (passwordError) setPasswordError(""); }}
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="mb-3 text-start">
                  <label className="form-label fw-semibold">{t("resetPassword.confirmPassword")}</label>
                  <div className="input-group">
                    <span className="input-group-text"><i className="bi bi-lock-fill"></i></span>
                    <input
                      type="password"
                      className={`form-control${passwordError ? " is-invalid" : ""}`}
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => { setConfirmPassword(e.target.value); if (passwordError) setPasswordError(""); }}
                      disabled={loading}
                    />
                    {passwordError && <div className="invalid-feedback">{passwordError}</div>}
                  </div>
                </div>

                {error && (
                  <div className="alert alert-danger py-2 small mb-3">
                    <i className="bi bi-exclamation-circle me-1"></i>
                    {error}
                  </div>
                )}

                <button
                  type="button"
                  className="btn btn-danger w-100 rounded-pill py-2 fw-semibold"
                  onClick={handleSubmit}
                  disabled={!newPassword || !confirmPassword || loading}
                >
                  {loading ? (
                    <span className="spinner-border spinner-border-sm me-2" />
                  ) : (
                    <i className="bi bi-check-circle me-2"></i>
                  )}
                  {t("resetPassword.button")}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPopup;
